/**
 * @file     NumericField.tsx
 * @purpose  Token-styled numeric input with optional prefix/suffix used inside the rent vs buy input cards. Has visible focus ring and tabular numeric.
 */
'use client';

import { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { alpha } from '@mui/material/styles';
import { useAppTheme } from '@/core/theme/useAppTheme';

type Props = {
  label: string;
  value: number;
  onChange: (next: number) => void;
  prefix?: string;
  suffix?: string;
  step?: number;
  min?: number;
  max?: number;
  helper?: string;
};

// Format a number for display with locale thousands separators so values
// like 1200000 read as "1,200,000". Decimals are kept faithfully.
const formatDisplay = (n: number): string => {
  if (!Number.isFinite(n)) return '';
  return new Intl.NumberFormat('en-CA', { maximumFractionDigits: 4 }).format(n);
};

export const NumericField = ({ label, value, onChange, prefix, suffix, step, min, max, helper }: Props) => {
  const { theme } = useAppTheme();
  const [text, setText] = useState<string>(formatDisplay(value));
  const [focused, setFocused] = useState(false);

  // Keep displayed text in sync with external changes when not focused.
  // While focused we let the user type freely.
  useEffect(() => {
    if (!focused) setText(formatDisplay(value));
  }, [value, focused]);

  const commit = (raw: string) => {
    const cleaned = raw.replace(/[^0-9.\-]/g, '');
    if (cleaned === '' || cleaned === '-' || cleaned === '.') {
      onChange(0);
      return;
    }
    const n = Number(cleaned);
    if (Number.isFinite(n)) {
      let next = n;
      if (typeof min === 'number') next = Math.max(min, next);
      if (typeof max === 'number') next = Math.min(max, next);
      onChange(next);
    }
  };

  return (
    <Box>
      <Typography
        component="label"
        sx={{
          display: 'block',
          fontSize: theme.typography.bodySm,
          color: theme.semanticColors.textSecondary,
          fontWeight: 500,
          mb: 0.75,
        }}
      >
        {label}
      </Typography>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          bgcolor: theme.semanticColors.surfaceElevated,
          border: `1px solid ${focused ? theme.semanticColors.interactivePrimary : theme.semanticColors.borderSubtle}`,
          borderRadius: `${theme.layout.radius.sm}px`,
          px: 1.25,
          height: 42,
          transition: `border-color ${theme.motion.durations.fast}ms ${theme.motion.easing.standard}, box-shadow ${theme.motion.durations.fast}ms ${theme.motion.easing.standard}`,
          ...(focused
            ? { boxShadow: `0 0 0 3px ${alpha(theme.rawSemanticColors.interactivePrimary, 0.18)}` }
            : {}),
        }}
      >
        {prefix && (
          <Typography sx={{ fontSize: theme.typography.bodyMd, color: theme.semanticColors.textMuted, mr: 0.5, fontVariantNumeric: 'tabular-nums' }}>
            {prefix}
          </Typography>
        )}
        <Box
          component="input"
          type="text"
          inputMode="decimal"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={(e) => {
            setFocused(false);
            commit(e.target.value);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
          }}
          step={step}
          sx={{
            flex: 1,
            minWidth: 0,
            border: 'none',
            outline: 'none',
            background: 'transparent',
            fontFamily: 'inherit',
            fontSize: theme.typography.bodyMd,
            color: theme.semanticColors.textPrimary,
            fontVariantNumeric: 'tabular-nums',
            '::placeholder': { color: theme.semanticColors.textMuted },
          }}
        />
        {suffix && (
          <Typography sx={{ fontSize: theme.typography.bodySm, color: theme.semanticColors.textMuted, ml: 0.5, whiteSpace: 'nowrap' }}>
            {suffix}
          </Typography>
        )}
      </Box>
      {helper && (
        <Typography sx={{ fontSize: 12, color: theme.semanticColors.textMuted, mt: 0.5 }}>
          {helper}
        </Typography>
      )}
    </Box>
  );
};
